import { type InputHTMLAttributes, forwardRef, useId } from 'react';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  function FormField({ label, hint, error, id: idProp, ...inputProps }, ref) {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;
    const hasError = Boolean(error);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {/* Label */}
        <label
          htmlFor={id}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: hasError ? 'var(--color-text-danger)' : 'var(--color-text-secondary)',
            lineHeight: 'var(--leading-snug)',
          }}
        >
          {label}
          {inputProps.required && (
            <span
              aria-hidden="true"
              style={{ color: 'var(--color-text-danger)', marginLeft: 'var(--space-1)' }}
            >
              *
            </span>
          )}
        </label>

        {/* Input wrapper */}
        <div style={{ position: 'relative' }}>
          <input
            ref={ref}
            id={id}
            aria-invalid={hasError}
            aria-describedby={[hint ? hintId : '', hasError ? errorId : ''].filter(Boolean).join(' ') || undefined}
            {...inputProps}
            style={{
              display: 'block',
              width: '100%',
              height: 'var(--input-height)',
              padding: 'var(--input-padding-y) var(--input-padding-x)',
              border: `1px solid ${hasError ? 'var(--color-border-error)' : 'var(--input-border)'}`,
              borderRadius: 'var(--input-radius)',
              backgroundColor: 'var(--input-bg)',
              fontSize: 'var(--input-font-size)',
              fontFamily: 'var(--font-sans)',
              color: 'var(--color-text-primary)',
              outline: 'none',
              transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
              boxShadow: hasError ? 'var(--shadow-focus-danger)' : 'none',
              ...inputProps.style,
            }}
            onFocus={e => {
              e.target.style.borderColor = hasError
                ? 'var(--color-border-error)'
                : 'var(--input-border-focus)';
              e.target.style.boxShadow = hasError
                ? 'var(--shadow-focus-danger)'
                : 'var(--shadow-focus)';
              inputProps.onFocus?.(e);
            }}
            onBlur={e => {
              e.target.style.borderColor = hasError
                ? 'var(--color-border-error)'
                : 'var(--input-border)';
              e.target.style.boxShadow = hasError ? 'var(--shadow-focus-danger)' : 'none';
              inputProps.onBlur?.(e);
            }}
          />

          {/* Error icon */}
          {hasError && (
            <span style={{
              position: 'absolute',
              right: 'var(--space-3)',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-danger)',
              pointerEvents: 'none',
            }}>
              <IconAlertCircle />
            </span>
          )}
        </div>

        {/* Hint */}
        {hint && !hasError && (
          <p
            id={hintId}
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: 'var(--leading-normal)',
            }}
          >
            {hint}
          </p>
        )}

        {/* Error message */}
        {hasError && (
          <p
            id={errorId}
            role="alert"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-danger)',
              lineHeight: 'var(--leading-normal)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
            }}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

function IconAlertCircle() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}
